import { defineStore } from 'pinia';
import CreateMailoutApi from '@/api';
import { useHomeStore } from '@/store/home';
import { useFileUploadStore } from '@/store/address';
import useDeliveryStore from '@/store/delivery';
import { usePdfStore } from '@/store/pdf';
import { useTopbarStore } from '@/store/topbar';
import type { PriceRequest, PriceResponse, PriceApiResponse } from '@/types/price';
import { useStorage } from '@vueuse/core';

const createEmptyPriceData = (): PriceResponse => ({
  units: '',
  ISO_units: '',
  additionalServices: [],
  postage: [],
  baseProduct: {
    SAPproductId: '',
    name: '',
    count: 0,
    price: 0,
    priceWithVAT: 0,
    VAT: 0,
  },
  total: 0,
  totalWithVAT: 0,
  VATAmount: 0,
});

export const usePriceStore = defineStore('price', {
  state: () => ({
    priceData: useStorage<PriceResponse>('priceData', createEmptyPriceData()),
    hasPriceTierConfigurationError: false as boolean,
  }),

  getters: {
    printTotal: (state): number =>
      (state.priceData?.additionalServices ?? []).reduce((sum, item) => sum + item.price, 0),
    shippingTotal: (state): number =>
      (state.priceData?.postage ?? []).reduce((sum, item) => sum + item.price, 0),
  },

  actions: {
    resetPrice() {
      this.priceData = createEmptyPriceData();
    },

    setPriceTierConfigurationError() {
      this.hasPriceTierConfigurationError = true;
    },

    clearPriceTierConfigurationError() {
      this.hasPriceTierConfigurationError = false;
    },

    async fetchPrice() {
      const addressStore = useFileUploadStore();
      const deliveryStore = useDeliveryStore();
      const pdfStore = usePdfStore();

      try {
        const homeStore = useHomeStore();
        const topbarStore = useTopbarStore();

        if (!homeStore.serviceCard) {
          await homeStore.fetchServiceCard();
        }

        const selectedLanguage =
          topbarStore.topBar?.language || localStorage.getItem('language') || 'sv';
        const numberOfPages = Number.parseInt(pdfStore.pdfFileNumberOfPages, 10);

        const payload: PriceRequest = {
          productId: homeStore.serviceCard?.id || '6000',
          addressType: 'ADR',
          deliveryDate: deliveryStore.selectedDate || '',
          deliveryId: String(homeStore.serviceCard?.recipients || 100),
          numberOfPages: Number.isNaN(numberOfPages) ? 0 : numberOfPages,
          numberOfRecipients: addressStore.validation.validAddressCount || 0,
          country: 'SE',
          language: selectedLanguage,
          userType: 'company',
          addReferenceSamples: false,
          targetType: 'FILE',
        };

        const response = (await CreateMailoutApi.getPrice(payload)) as unknown as PriceApiResponse;
        this.priceData = response.result;
      } catch (err: unknown) {
        console.error('PriceStore: error loading price data', err);
        throw err;
      }
    },
  },
});
